import * as React from 'react';
import { connect } from 'react-redux';

import FclDetails from './FclDetails';
import LclDetails from './LclDetails';
import AirCargoDetails from './AirCargoDetails';
import { prettifyInquiryMode } from '../../InquiryUtils';
import { InquiryModes } from 'src/Library/Constants';
import { tokens } from 'src/design-system';

// Helper function to extract container data
const extractContainerData = (data) => {
  // Prioritize inquiryMode if it's RPTL, otherwise use mode
  const mode = data.inquiryMode ===  InquiryModes.RPTL || data.mode ===  InquiryModes.RPTL
    ?  InquiryModes.RPTL
    : (data.mode || data.inquiryMode);
  return {
    containerDetails: data.containerDetails || data.inquiryContainerDetails,
    mode,
    type: data.type || data.inquiryType,
    cargoType: data.cargoType || data.inquiryCargoType
  };
};

// Helper function to get container types based on mode
const getContainerTypesByMode = (mode, vehicleTypeSizeList, domesticVehicleTypeSizeList, containerList) => {
  const isDomesticMode = mode === InquiryModes.DOMESTIC || mode ===  InquiryModes.RPTL;
  const containerTypeMap = {
    [InquiryModes.TRAILER]: vehicleTypeSizeList,
    [InquiryModes.DOMESTIC]: domesticVehicleTypeSizeList,
    [ InquiryModes.RPTL]: domesticVehicleTypeSizeList
  };
  
  return isDomesticMode ? domesticVehicleTypeSizeList : (containerTypeMap[mode] || containerList);
};

// Helper function to render subview component
const renderSubView = (mode, containerDetails, props) => {
  const { lclPackageTypes, airCargoPackageTypes, containerTypes } = props;
  const isDomesticMode = mode === InquiryModes.DOMESTIC || mode ===  InquiryModes.RPTL;

  
  const componentMap = {
    [InquiryModes.FCL]: (
      <FclDetails 
        containerDetails={containerDetails}
        getContainerType={getContainerType(containerTypes)} 
      />
    ),
    [InquiryModes.TRAILER]: (
      <FclDetails 
        containerDetails={containerDetails}
        getContainerType={getContainerType(containerTypes)} 
      />
    ),
    [InquiryModes.LCL]: (
      <LclDetails 
        containerDetails={containerDetails}
        lclPackageTypes={lclPackageTypes}
      />
    ),
    [InquiryModes.AIRCARGO]: (
      <AirCargoDetails 
        containerDetails={containerDetails}
        airCargoPackageTypes={airCargoPackageTypes}
      />
    ),
    [InquiryModes.RPTL]: (
      <LclDetails 
        containerDetails={containerDetails}
        lclPackageTypes={lclPackageTypes}
      />
    ),
  };
  
  return componentMap[mode] || null;
};

// Helper function to get mode display text
const getModeDisplayText = (mode, showRoadInsteadOfDomestic) => {
  // Check for RPTL first before checking DOMESTIC
  if (mode ===  InquiryModes.RPTL) {
    return  InquiryModes.RPTL;
  }
  if (mode === InquiryModes.DOMESTIC && showRoadInsteadOfDomestic) {
    return 'Road';
  }
  return prettifyInquiryMode[mode] || mode;
};

// Helper function to check if type should be displayed
const shouldDisplayType = (type, mode) => {
  const isDomesticMode = mode === InquiryModes.DOMESTIC || mode ===  InquiryModes.RPTL;
  return type && !isDomesticMode;
};

const ContainerDetails = (props) => {
  const {
    data,
    lclPackageTypes,
    airCargoPackageTypes,
    containerList,
    vehicleTypeSizeList,
    domesticVehicleTypeSizeList,
    modules
  } = props;

  const { containerDetails, mode, type, cargoType } = extractContainerData(data);
  
  if (!containerDetails) return null;

  const containerTypes = getContainerTypesByMode(
    mode, 
    vehicleTypeSizeList, 
    domesticVehicleTypeSizeList, 
    containerList
  );

  const subView = renderSubView(mode, containerDetails, {
    lclPackageTypes,
    airCargoPackageTypes,
    containerTypes
  });

  const modeDisplayText = getModeDisplayText(mode, modules?.show_road_instead_of_domestic);
  const displayType = shouldDisplayType(type, mode);

  return (
    <div style={{ paddingLeft: '5px', paddingRight: '5px', position: 'relative' }}>
      <div style={{ color: '#262626' }}>
        <span style={{ fontSize: `${tokens.fontSize.body}px`, marginRight: '8px', fontWeight: 600 }}>
          {modeDisplayText}
        </span>
        {displayType && (
          <span style={{ fontSize: `${tokens.fontSize.small}px` }}>
            ({type[0]}{type.slice(1).toLowerCase()})
          </span>
        )}
      </div>
      <div style={{
        position: 'absolute',
        left: '50%',
        textAlign: 'center',
        transform: 'translateX(-50%)',
        whiteSpace: 'nowrap',
      }}>
        {cargoType && (
          <span style={{ fontSize: `${tokens.fontSize.small}px`, padding: '10px' }}>
            Cargo Type : {cargoType}
          </span>
        )}
        {subView}
      </div>
    </div>
  );
};

const getContainerType = containerTypes => (key) => {
  if (!key || !containerTypes) return;
  const type = containerTypes.find(item => item.key.type === key)?.label?.type;
  return type;
};

const mapStateToProps = ({
  masterData: { containerList, vehicleTypeSizeList, domesticVehicleTypeSizeList, modules },
  inquiryReducer: { masterData: { lclPackageTypes, airCargoPackageTypes } },
}) => ({
  lclPackageTypes,
  airCargoPackageTypes,
  containerList,
  vehicleTypeSizeList,
  domesticVehicleTypeSizeList,
  modules,
});

export default connect(mapStateToProps)(ContainerDetails);